import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { focusRing } from "@ui/tokens/interaction";

type ToastVariant = "success" | "error" | "info";

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  onDismiss?: () => void;
}

const ICONS: Record<ToastVariant, typeof CheckCircleIcon> = {
  success: CheckCircleIcon,
  error: ExclamationCircleIcon,
  info: InformationCircleIcon,
};

const TONE: Record<ToastVariant, string> = {
  success: "bg-success-bg text-success",
  error: "bg-danger-bg text-danger",
  info: "bg-surface-icon-tile text-ink-body",
};

const EDGE: Record<ToastVariant, string> = {
  success: "border-success-line",
  error: "border-danger-line",
  info: "border-line-soft",
};

const touchTarget =
  "relative after:absolute after:left-1/2 after:top-1/2 after:w-touch after:h-touch after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']";

/**
 * One line, one icon, one way out. A toast never asks for a decision — that is what a modal is
 * for — so it carries no title and no footer, just the fact, coloured by what kind of fact it is.
 *
 * White reads as the rest of the interface; what makes this one hard to miss is the same modal
 * shadow that lifts an overlay off the page, plus a hairline in the variant's own colour.
 */
export const Toast = ({ message, variant = "info", onDismiss }: ToastProps) => {
  const Icon = ICONS[variant];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.96 }}
      transition={{ duration: 0.24, ease: [0.2, 0.7, 0.2, 1] }}
      role="status"
      className={`flex items-center gap-3 w-full max-w-toast px-4.5 py-3.5 rounded-card bg-surface-raised shadow-modal border ${EDGE[variant]}`}
    >
      <span
        className={`flex items-center justify-center shrink-0 w-9 h-9 rounded-pill ${TONE[variant]}`}
      >
        <Icon className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
      </span>
      <span className="flex-1 min-w-0 text-control font-600 text-ink">{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fermer"
          className={`flex items-center justify-center shrink-0 w-6 h-6 rounded-xs text-ink-meta hover:bg-row-hover hover:text-ink-body transition-colors duration-hover ${touchTarget} ${focusRing}`}
        >
          <XMarkIcon className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      )}
    </motion.div>
  );
};
